import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import {
  ApiResponse,
  Conversation,
  ConversationsResponse,
  SendMessageRequest,
  EditMessageRequest,
  MarkReadRequest,
  DeleteMessageRequest,
  DeleteConversationRequest,
  MessagesResponse,
  Message,
  SharedDocument,
  SharedDocumentsResponse,
  CreateEventGroupConversationRequest
} from '../interface/vendor-chat.interface';

@Injectable({
  providedIn: 'root'
})
export class VendorChatService {
  private readonly apiUrl = environment.apiUrl;
  private readonly baseRoute = 'vendor-chat';
  private readonly skipLoadingHeaders = new HttpHeaders({ 'Skip-Loading': 'true' });

  constructor(private readonly http: HttpClient) {}

  private getUrl(route: string): string {
    return `${this.apiUrl}/${this.baseRoute}/${route}`;
  }

  /**
   * Send a message to a conversation
   */
  sendMessage(payload: SendMessageRequest | FormData): Observable<ApiResponse<{ message: Message }>> {
    return this.http.post<ApiResponse<{ message: Message }>>(
      this.getUrl('message'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Edit a message in a conversation
   */
  editMessage(payload: EditMessageRequest): Observable<ApiResponse<{ message: Message }>> {
    return this.http.put<ApiResponse<{ message: Message }>>(
      this.getUrl('message'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Delete a message from a conversation
   */
  deleteMessage(payload: DeleteMessageRequest): Observable<ApiResponse<{ message_id: number }>> {
    return this.http.delete<ApiResponse<{ message_id: number }>>(
      this.getUrl('message'),
      { body: payload, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Fetch user/vendor conversations
   */
  getConversations(params?: HttpParams): Observable<ApiResponse<ConversationsResponse>> {
    return this.http.get<ApiResponse<ConversationsResponse>>(
      this.getUrl('conversations'),
      { params, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Fetch messages for a conversation
   */
  getMessages(conversationId: number, params?: HttpParams): Observable<ApiResponse<MessagesResponse>> {
    const queryParams = params || new HttpParams();
    const finalParams = queryParams.set('conversation_id', conversationId.toString());
    
    return this.http.get<ApiResponse<MessagesResponse>>(
      this.getUrl('messages'),
      { params: finalParams, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Mark messages as read in a conversation
   */
  markMessagesAsRead(payload: MarkReadRequest): Observable<ApiResponse<{ updated_count: number }>> {
    return this.http.post<ApiResponse<{ updated_count: number }>>(
      this.getUrl('messages/mark-read'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Delete a conversation
   */
  deleteConversation(payload: DeleteConversationRequest): Observable<ApiResponse<{ conversation_id: number }>> {
    return this.http.delete<ApiResponse<{ conversation_id: number }>>(
      this.getUrl('conversation'),
      { body: payload, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Get all shared documents/files in a conversation
   */
  getSharedDocuments(conversationId: number, params?: HttpParams): Observable<ApiResponse<SharedDocumentsResponse>> {
    const queryParams = params || new HttpParams();
    const finalParams = queryParams.set('conversation_id', conversationId.toString());
    return this.http.get<ApiResponse<SharedDocumentsResponse>>(
      this.getUrl(`conversations/${conversationId}/documents`),
      { params: finalParams, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Create event group conversation
   */
  createEventGroupConversation(payload: CreateEventGroupConversationRequest): Observable<ApiResponse<{ conversation: Conversation }>> {
    return this.http.post<ApiResponse<{ conversation: Conversation }>>(
      this.getUrl('conversation/event-group'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }
}

